import React, { useState } from "react";
import { Row, Col, Table, notification } from "antd";
import {
  ColumnGroupType,
  ColumnType,
  TablePaginationConfig,
} from "antd/es/table";
import styles from "./ServicesTable.module.scss";
import textStyles from "../../Shared/Styles/TextStyles.module.scss";
import { useTripsCtx } from "../../../context/TripDetailsContext";
import Spinner from "../../../assets/Loader/GoogleSvgLoader/GoogleLoader";
import { useHistory, useLocation } from "react-router-dom";
import {
  IFavRecords,
  ITripDetails,
} from "../../ServiceUsers/Interfaces/ITripDetails";
import { TrackAnalytics } from "../../../types/ApplicationTypes";
import { Tabs } from "../../../types/TripPaginationFilterTypes";
import {
  RebookTripOptions,
  useTripActions,
} from "../../../hooks/useTripActions";
import { dashboardRebookMessage } from "../../../types/TripTypes";
import { defaultTripDetails } from "../../ServiceUsers/ServiceUsersMobile/defaultTripDetails";
import { useWindowSize } from "../../../hooks/useWindowSize";
import { useDashboardCtx } from "../../../context/DashboardContext";
import CancelTripModal from "../Modals/CancelTripModal";
import DashboardFavoriteModal from "../DashboardFavoriteModal";
import RemoveFavoriteModal from "../../Shared/RemoveFavoriteModal";
import RenameFavoriteModal from "../Modals/RenameFavoriteModal";
import TripDetailsDrawer from "../TripDetails/TripDetailsDrawer";
import { getServiceColumns } from "./tableColumns";
import { getTripByRequestAssignmentId } from "../../../services";
import moment from "moment";

type TripsColumnType = {
  key?: React.Key;
  tripDate?: string;
  serviceUser?: string;
  tripCost?: number;
  tripReason?: string;
  favoriteName?: string;
};

type Props = {
  dataSource: ITripDetails[];
  paginationConfig: TablePaginationConfig;
  handleServiceUser: (personId: number) => void;
};

function ServicesTable({
  dataSource,
  paginationConfig,
  handleServiceUser,
}: Props) {
  const [isFavModalVisible, setIsFavModalVisible] = useState<boolean>(false);
  const [cancelTripRecord, setCancelTripRecord] = useState<ITripDetails>(
    defaultTripDetails
  );
  const [favRecords, setFavRecords] = useState<IFavRecords>();
  const [favoriteTripsToRemove, setFavoriteTripsToRemove] = useState<
    ITripDetails[]
  >([]);
  const [isRemoveModalVisible, setIsRemoveModalVisible] = useState<boolean>(
    false
  );
  const [isRenameModalVisible, setIsRenameModalVisible] = useState<boolean>(
    false
  );
  const [isCancelTripModalVisible, setIsCancelTripModalVisible] = useState<
    boolean
  >(false);
  const [selectedLeg, setSelectedLeg] = useState<ITripDetails>(
    defaultTripDetails
  );
  const [isOpenTripDetailsDrawer, setIsOpenTripDetailsDrawer] = useState<
    boolean
  >(false);
  const [favoriteTrip, setFavoriteTrip] = useState<ITripDetails[]>([]);
  const [isLoadingFavTrip, setIsLoadingFavTrip] = useState<boolean>(false);
  const [updatedFavName, setUpdateFavName] = useState<string>("");

  const history = useHistory();
  const { pathname } = useLocation();

  const {
    isLoading,
    upcomingTripDetailsCtx,
    pastTripDetailsCtx,
    favoriteTripDetailsCtx,
  } = useTripsCtx();

  const [upcomingTripDetails, setUpcomingTripDetails] = upcomingTripDetailsCtx;
  const [pastTripDetails] = pastTripDetailsCtx;
  const [favoriteTripDetails] = favoriteTripDetailsCtx;

  const {
    activeTabCtx,
    sortOrderCtx,
    showCanceledCtx,
    selectedPersonIdCtx,
    resetFavoritePagination,
  } = useDashboardCtx();
  const [activeTab] = activeTabCtx;
  const [showCanceled] = showCanceledCtx;
  const [, setSortOrder] = sortOrderCtx;
  const [selectedPersonId] = selectedPersonIdCtx;

  const {
    rebookTrip,
    cancelTrip,
    removeFavoriteTrip,
    renameFavoriteTrip,
  } = useTripActions();

  const { width } = useWindowSize();

  function handleSortToggle() {
    setSortOrder((prevOrder: { [tab: string]: string }) => ({
      ...prevOrder,
      [activeTab]: prevOrder[activeTab] === "DESC" ? "ASC" : "DESC",
    }));
  }

  function handleRebook(trip: RebookTripOptions) {
    const event = dashboardRebookMessage[activeTab];
    TrackAnalytics("/DashBoard", event, "DashBoard Page");
    rebookTrip(trip);
  }

  function handleAddFavorite(record: ITripDetails): void {
    const totalTrips = [...upcomingTripDetails, ...pastTripDetails];
    const allTripLegs = totalTrips.filter(
      (trip: ITripDetails) =>
        trip.requestAssignmentId === record.requestAssignmentId
    );
    const data = {
      selectedLeg: [record],
      allLegs: allTripLegs,
      legsCount: allTripLegs.length,
    };
    setFavRecords(data);
    setIsFavModalVisible(true);
  }

  function handleRemoveFavorite(selectedTrip: ITripDetails) {
    const tripLegsToRemove = favoriteTripDetails.filter(
      (trip: ITripDetails) => trip.id === selectedTrip.id
    );
    setFavoriteTripsToRemove(tripLegsToRemove);
    setIsRemoveModalVisible(true);
  }

  function handleCancelService(record: ITripDetails) {
    setCancelTripRecord(record);
    setIsCancelTripModalVisible(true);
  }

  function shouldHideColumn(
    column: ColumnGroupType<TripsColumnType> | ColumnType<TripsColumnType>
  ): boolean {
    if (selectedPersonId && column.key === "serviceUser") {
      return false;
    }

    if (width < 992) {
      if (
        column.key === "actions" ||
        (activeTab !== Tabs.PAST && column.key === "tripStatus") ||
        (activeTab === Tabs.PAST &&
          !showCanceled &&
          column.key === "tripStatus") ||
        (!selectedPersonId && column.key === "tripCost") ||
        (activeTab === Tabs.FAVORITE &&
          (column.key === "serviceUser" || column.key === "tripReason"))
      )
        return false;
    }
    return true;
  }

  function handleRenameFavorite(record: ITripDetails) {
    setSelectedLeg(record);
    setIsRenameModalVisible(true);
    setUpdateFavName(record.favoriteName);
  }

  function handleYesRemove() {
    removeFavoriteTrip(favoriteTripsToRemove);
    setIsRemoveModalVisible(false);
    setIsOpenTripDetailsDrawer(false);
    resetFavoritePagination();
  }

  function handleYesCancel() {
    cancelTrip(cancelTripRecord);
    TrackAnalytics("/DashBoard", "Trip is cancelled", "Dashboard Page");
    const filterCanceledTrips = upcomingTripDetails.filter(
      (upcomingTrip: ITripDetails) =>
        upcomingTrip.requestAssignmentId !==
        cancelTripRecord.requestAssignmentId
    );
    setUpcomingTripDetails(filterCanceledTrips);
    setIsOpenTripDetailsDrawer(false);
    setIsCancelTripModalVisible(false);
  }

  async function getFavoriteTripDetails(leg: ITripDetails) {
    try {
      const totalTrips = await getTripByRequestAssignmentId({
        requestAssignmentId: Number(leg.requestAssignmentId),
        personId: Number(leg.personId),
        favoriteId: Number(leg.favoriteId),
        tripDate: moment.utc(leg.tripDate).format(),
      });

      return totalTrips.personTrips.sort(
        (tripA: ITripDetails, tripB: ITripDetails) =>
          tripA.bookingId.localeCompare(tripB.bookingId)
      );
    } catch (error) {
      throw error;
    }
  }

  async function handleSaveRenamedFav() {
    try {
      if (isOpenTripDetailsDrawer) {
        await renameFavoriteTrip(updatedFavName, favoriteTrip);
      } else {
        const favoriteTrip = await getFavoriteTripDetails(selectedLeg);
        await renameFavoriteTrip(updatedFavName, favoriteTrip);
        setFavoriteTrip(favoriteTrip);
      }

      setFavoriteTrip((trip) =>
        trip.map((leg) => ({
          ...leg,
          favoriteName: updatedFavName,
        }))
      );
      setSelectedLeg((leg) => ({
        ...leg,
        favoriteName: updatedFavName,
      }));
      setIsRenameModalVisible(false);
    } catch (error) {
      notification.error({
        message: "Failed to Rename Favorite",
      });
    }
  }

  async function handleTripDetails(record: ITripDetails) {
    const isFavTripAvailable =
      record.favoriteId === favoriteTrip[0]?.favoriteId &&
      record.requestAssignmentId === favoriteTrip[0]?.requestAssignmentId;
    const isFavTab = activeTab === Tabs.FAVORITE;
    setIsOpenTripDetailsDrawer(true);

    if (isFavTab && !isFavTripAvailable) {
      try {
        setIsLoadingFavTrip(true);
        const favoriteTrip = await getFavoriteTripDetails(record);
        setFavoriteTrip(favoriteTrip);
      } catch (error) {
        notification.error({
          message: "Failed to Fetch Favorite Trip",
        });
      } finally {
        setIsLoadingFavTrip(false);
      }
    }
    isFavTab && setUpdateFavName(record.favoriteName || "");
    setSelectedLeg(record);
  }

  const actionHandlers = {
    handleAddFavorite,
    handleRebook,
    handleCancelService,
    handleRemoveFavorite,
    handleRenameFavorite,
  };

  const columnHandlers = {
    activeTab,
    handleSortToggle,
    handleServiceUser,
    handleTripDetails,
    shouldHideColumn,
    actionHandlers,
  };

  const columns = getServiceColumns(columnHandlers);

  function getTableContainerHeight() {
    if (activeTab === Tabs.FAVORITE && !favoriteTripDetails.length) {
      return "";
    } else {
      return selectedPersonId
        ? styles.isSelectedPersonTableHeight
        : styles.isAllPersonsTableHeight;
    }
  }

  return isLoading ? (
    <Spinner />
  ) : (
    <>
      <Row className={`${styles.tableContainer} ${getTableContainerHeight()}`}>
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={paginationConfig}
          scroll={{ y: selectedPersonId !== "" ? 484 : 530 }}
        />
        {activeTab === Tabs.FAVORITE && !favoriteTripDetails?.length && (
          <Row
            className={`${textStyles.secondaryNormalText14} ${styles.favoriteInfoAlign}`}
            gutter={[0, 32]}
          >
            <Col span={24}>
              Services that you use often can be saved as Favorites, so you
              don't have to re-enter the service details every time you book
              them.
            </Col>
            <Col span={24}>
              Rebook will allow you to review and edit service.
            </Col>
          </Row>
        )}
      </Row>
      <TripDetailsDrawer
        openTripDetails={isOpenTripDetailsDrawer}
        setOpenTripDetails={setIsOpenTripDetailsDrawer}
        selectedLeg={selectedLeg}
        actionHandlers={actionHandlers}
        favoriteTrip={favoriteTrip}
        isLoadingFavTrip={isLoadingFavTrip}
      />
      <CancelTripModal
        visible={isCancelTripModalVisible}
        setVisibility={setIsCancelTripModalVisible}
        handleYesCancel={handleYesCancel}
      />
      <DashboardFavoriteModal
        favModalVisible={isFavModalVisible}
        favRecords={favRecords}
        setFavModalVisible={setIsFavModalVisible}
        isDesktop
      />
      <RenameFavoriteModal
        visible={isRenameModalVisible}
        setVisibility={setIsRenameModalVisible}
        updatedFavName={updatedFavName}
        setUpdateFavName={setUpdateFavName}
        handleSaveRenamedFav={handleSaveRenamedFav}
      />
      <RemoveFavoriteModal
        isRemoveModalVisible={isRemoveModalVisible}
        setIsRemoveModalVisible={setIsRemoveModalVisible}
        handleYesRemove={handleYesRemove}
      />
    </>
  );
}

export default ServicesTable;
